/**
 * Shared types between apps/api and apps/web. No runtime imports allowed —
 * only `type` exports + `const` literal unions. Anything that needs a value
 * (Zod schemas, hasFeature, vendor list, ...) lives in dedicated files.
 */
// ── Account ────────────────────────────────────────────────────────────
export const TIERS = ['free', 'paid'];
/** `operator` sees all clients' objects (release 1 operator list). */
export const ROLES = ['client', 'operator'];
// ── Site ───────────────────────────────────────────────────────────────
export const SITE_MODES = ['internal', 'external'];
export const SCHEDULE_MODES = ['manual', 'auto'];
export const STRATEGIES = ['manual', 'internal_tariff', 'dam_optimizer'];
/** Client tariff on the object: fixed rate, 2- or 3-zone, hourly day-ahead price. */
export const TARIFF_TYPES = ['fixed', 'zone2', 'zone3', 'dam'];
/** Current transformer at the object's grid input: «є / немає / не знаю». */
export const INPUT_CT_VALUES = ['yes', 'no', 'unknown'];
/**
 * D3/D4: access of Phage.Energy's business account to the object's station in
 * DeyeCloud — every DeyeCloud call of the object goes through that account:
 * «Очікує доступу» (not seen in the company's list yet: no call), «Доступ
 * відкрито», «Доступ закрито» (seen before, gone now: no poll, download or
 * arbitrage), «Немає права керування» (DeyeCloud refused a write for the
 * rights: data goes on, internal arbitrage is off).
 */
export const PHAGE_ACCESS_STATUSES = ['pending', 'open', 'closed', 'noedit'];
/** `dam_prices.source` of day-ahead prices entered by the operator («вручну»). */
export const DAM_SOURCE_MANUAL = 'manual';
/**
 * Mode of a Deye TOU window: «Заряд з мережі» (charge from the grid up to the
 * window's SOC), «Власне споживання» (discharge into the object's load, not
 * below the window's SOC), «Утримання заряду» (no discharge; PV still charges).
 */
export const DEYE_WINDOW_MODES = ['grid_charge', 'self_consumption', 'hold'];
/** Deye runs exactly this many TOU windows a day. */
export const DEYE_WINDOWS_PER_DAY = 6;
/** What an entry of the object's journal (`site_changes`) records. */
export const SITE_CHANGE_ACTIONS = [
    'settings',
    'tariff',
    'load_profile',
    'internal_arbitrage',
    'windows',
    'analysis_restart',
    'disconnect',
    // «Повернути програму Phage» after the program was changed by hand (25.10.2026).
    'program_resumed',
];
// ── Object analysis (release 1) ────────────────────────────────────────
export const JOB_TYPES = [
    'analysis.backfill',
    'analysis.daily',
    'analysis.profile',
    'analysis.potential',
    /** An inverter write started in a process whose writes are off, done by the worker (EKS). */
    'inverter.dispatch',
];
export const ANALYSIS_STATUSES = ['pending', 'running', 'done', 'failed'];
export const POTENTIAL_STATUSES = ['ok', 'no_osr_rate', 'no_data'];
/**
 * Canonical cumulative counters and gauges read from the vendor history
 * (DEYE `historyRaw`). Hourly energies are differences of the counters.
 * Device key names differ per inverter family — the adapter maps them.
 * `load_kw` — consumption power, kW (D2: the consumption profile is built
 * from it; the counters step by 0.1 kWh, too coarse for a small load).
 */
export const HISTORY_POINTS = [
    'buy_kwh',
    'sell_kwh',
    'load_kwh',
    'pv_kwh',
    'charge_kwh',
    'discharge_kwh',
    'soc_pct',
    'load_kw',
];
// Vendors registered for MVP (Day 2 = DEYE only, others stub/coming-soon UI).
export const VENDORS = ['deye', 'huawei', 'growatt', 'solax', 'foxess', 'alpha'];
// ── Schedules ──────────────────────────────────────────────────────────
export const SCHEDULE_STATUSES = ['draft', 'approved', 'cancelled'];
//# sourceMappingURL=types.js.map